import { DateTimePicker12 } from '../components/TimePicker12';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Form, Spinner, Table } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { getMyVisits, requestReschedule, sendVisitReminder } from '../api';
import VisitTimeline from '../components/VisitTimeline';
import VisitCancelModal from '../components/VisitCancelModal';
import MapLink from '../components/MapLink';

const STATUS_VARIANT = { confirmed: 'success', cancelled: 'danger', pending: 'warning' };

export default function MyVisits() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reminded, setReminded] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [rescheduleFormId, setRescheduleFormId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleNote, setRescheduleNote] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [cancelVisitId, setCancelVisitId] = useState(null); // Story 1: popup instead of window.prompt

  const load = useCallback(() => {
    setLoading(true);
    return getMyVisits()
      .then(setVisits)
      .catch(() => setError('Could not load your visit requests.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRemind = async (id) => {
    setReminded((prev) => ({ ...prev, [id]: 'sending' }));
    try {
      await sendVisitReminder(id);
      setReminded((prev) => ({ ...prev, [id]: 'sent' }));
    } catch {
      setReminded((prev) => ({ ...prev, [id]: 'error' }));
    }
  };

  // Owner Bookings & Cancellation v2 doc, Story 1: a reason is required, plus explicit acceptance
  // of the cancellation terms - both captured in VisitCancelModal's popup rather than a bare
  // window.prompt(). Visits carry no rentalAmount, so there's no refund breakdown to preview
  // first (unlike the other 3 verticals' CancelBookingModal).
  const handleCancelled = () => {
    setCancelVisitId(null);
    setActionMsg('Visit request cancelled.');
    load();
  };

  const handleRescheduleSubmit = async (e, id) => {
    e.preventDefault();
    setBusyId(id);
    setActionErr('');
    try {
      await requestReschedule(id, { requestedEndDate: new Date(rescheduleDate).toISOString(), note: rescheduleNote });
      setActionMsg('Reschedule request sent to the owner.');
      setRescheduleFormId(null);
      setRescheduleDate('');
      setRescheduleNote('');
      await load();
    } catch (err) {
      setActionErr(err.response?.data?.msg || 'Unable to request a reschedule.');
    } finally {
      setBusyId(null);
    }
  };

  const visitsPaged = usePagedArray(visits);

  if (loading) return <Spinner animation="border" role="status" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div>
      <h3 className="mb-3">My visit requests</h3>
      {actionMsg && <Alert variant="success" dismissible onClose={() => setActionMsg('')}>{actionMsg}</Alert>}
      {actionErr && <Alert variant="danger" dismissible onClose={() => setActionErr('')}>{actionErr}</Alert>}
      {visitsPaged.total === 0 ? (
        <Alert variant="light">
          You haven't requested any property visits yet. <Link to="/">Browse properties</Link>.
        </Alert>
      ) : (
        <>
        <Table responsive hover>
          <thead>
            <tr>
              <th>Requested date &amp; time</th>
              <th>Property</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visitsPaged.rows.map((visit) => {
              const canCancel = ['pending', 'confirmed'].includes(visit.status)
                && visit.returnStatus !== 'COMPLETED'
                && new Date(visit.startDate) > new Date();
              const canReschedule = canCancel && !visit.extension?.requested;
              return (
                <tr key={visit._id}>
                  <td>{new Date(visit.startDate).toLocaleString()}</td>
                  <td>
                    <div className="fw-semibold">{visit.propertyName || 'Property'}</div>
                    {visit.propertyLocation && <div className="small text-muted">{visit.propertyLocation}</div>}
                    {visit.notes && <div className="small text-muted">Notes: {visit.notes}</div>}
                    {/* story 9 - the customer only ever sees the approximate area here. */}
                    <MapLink address={visit.siteAddress} fallbackText={visit.propertyLocation} title="Approximate location" approximate />
                  </td>
                  <td>
                    <Badge bg={STATUS_VARIANT[visit.status] || 'secondary'} className="mb-1">
                      {visit.status}
                    </Badge>
                    <VisitTimeline visit={visit} />
                    {['pending', 'confirmed'].includes(visit.status) && visit.returnStatus !== 'COMPLETED' && (
                      <div className="small text-muted mt-1">
                        {visit.deliveryAssignment?.assignedTo
                          ? `${typeof visit.deliveryAssignment.assignedTo === 'object' ? visit.deliveryAssignment.assignedTo.name : 'An agent'} will call you around the visit time`
                          : 'The owner or their agent will call you around the visit time'}
                        {visit.deliveryAssignment?.scheduledAt && ` (${new Date(visit.deliveryAssignment.scheduledAt).toLocaleString()})`}
                      </div>
                    )}
                    {visit.status === 'cancelled' && visit.cancellation?.reason && (
                      // Owner Bookings & Cancellation v2 doc, Story 12 - symmetric with the
                      // owner's own view in OwnerDashboard.js. Visits carry no rentalAmount, so
                      // there's no refund/settlement to show here.
                      <div className="small text-muted mt-1">
                        Cancelled by {visit.cancellation.cancelledBy}
                        {visit.cancellation.cancelledAt && ` on ${new Date(visit.cancellation.cancelledAt).toLocaleString()}`}
                        : "{visit.cancellation.reason}"
                      </div>
                    )}
                    {visit.extension?.requested && visit.extension.status === 'pending' && (
                      <div className="small text-muted mt-1">
                        Reschedule requested to {new Date(visit.extension.requestedEndDate).toLocaleString()} (awaiting owner)
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      {visit.status === 'pending' &&
                        (reminded[visit._id] === 'sent' ? (
                          <small className="text-success">Reminder sent</small>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={reminded[visit._id] === 'sending'}
                            onClick={() => handleRemind(visit._id)}
                          >
                            Remind owner
                          </Button>
                        ))}
                      {canReschedule && (
                        <Button size="sm" variant="outline-primary" onClick={() => setRescheduleFormId(visit._id)}>
                          Request different time
                        </Button>
                      )}
                      {canCancel && (
                        <Button size="sm" variant="outline-danger" onClick={() => setCancelVisitId(visit._id)}>
                          Cancel
                        </Button>
                      )}
                      {rescheduleFormId === visit._id && (
                        <Form onSubmit={(e) => handleRescheduleSubmit(e, visit._id)} className="border rounded p-2 mt-1">
                          <Form.Group className="mb-2">
                            <Form.Label className="small">New visit date &amp; time</Form.Label>
                            <DateTimePicker12
                              
                              required
                              min={new Date().toISOString().slice(0, 16)}
                              value={rescheduleDate}
                              onChange={(e) => setRescheduleDate(e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group className="mb-2">
                            <Form.Label className="small">Note (optional)</Form.Label>
                            <Form.Control as="textarea" rows={2} value={rescheduleNote} onChange={(e) => setRescheduleNote(e.target.value)} />
                          </Form.Group>
                          <Button size="sm" type="submit" disabled={busyId === visit._id}>Send request</Button>
                        </Form>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Pager {...visitsPaged} />
        </>
      )}

      <VisitCancelModal
        visitId={cancelVisitId}
        show={!!cancelVisitId}
        onHide={() => setCancelVisitId(null)}
        onCancelled={handleCancelled}
      />
    </div>
  );
}
